import { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { EldVehicle } from 'src/models';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { EldDriverStatusLabel } from 'src/components/eld-driver-status-label';
import { formatTimeAsHourMin } from 'src/utils/date-utils';
import TruxFormField from 'src/components/inputs/trux-form-field';
import TruxSimpleDateTimeField from 'src/components/inputs/trux-simple-date-time-field';
import TruxSimpleSelectField from 'src/components/inputs/trux-simple-select-field';
import TruxSimpleDecimalTwoField from 'src/components/inputs/trux-simple-decimal-two-field';
import {
  newTripStop,
  PickTarget,
  RouteBuddyDriverStatus,
  SimClocks,
  STOP_LABELS,
  StopLabel,
  StopLocation,
  TripStop,
} from './types';
import LocationField from './location-field';

const LOCATION_STALE_MINUTES = 30;

export function driverLabel(driver: RouteBuddyDriverStatus) {
  const unit = driver.eldVehicleUnitNumber ? ` (#${driver.eldVehicleUnitNumber})` : '';
  return `${driver.firstName} ${driver.lastName}${unit}`;
}

function ClockChip({ label, hours }: { label: string; hours: number }) {
  let background = '#36b746';
  if (hours < 2) {
    background = 'orange';
  }
  if (hours < 0.5) {
    background = '#ff5631';
  }
  return (
    <Label sx={{ color: 'white', background }}>{`${label} ${formatTimeAsHourMin(hours)}`}</Label>
  );
}

type PlanFormProps = {
  driverStatuses: RouteBuddyDriverStatus[];
  eldVehicles: EldVehicle[];
  vehicleId: number | null;
  setVehicleId: (vehicleId: number | null) => void;
  selectedDriver: RouteBuddyDriverStatus | null;
  setDriverId: (driverId: number | null) => void;
  originOverride: StopLocation;
  setOriginOverride: (value: StopLocation) => void;
  tripDepartAt: string | null;
  setTripDepartAt: (value: string | null) => void;
  adjustHours: boolean;
  setAdjustHours: (value: boolean) => void;
  clockEdits: SimClocks;
  setClockEdits: (value: SimClocks) => void;
  stops: TripStop[];
  setStops: (stops: TripStop[]) => void;
  picking: PickTarget;
  setPicking: (value: PickTarget) => void;
  timezone: string;
  simulating: boolean;
  onRun: () => void;
  canSave: boolean;
  onSave: () => void;
};

export default function PlanForm({
  driverStatuses,
  eldVehicles,
  vehicleId,
  setVehicleId,
  selectedDriver,
  setDriverId,
  originOverride,
  setOriginOverride,
  tripDepartAt,
  setTripDepartAt,
  adjustHours,
  setAdjustHours,
  clockEdits,
  setClockEdits,
  stops,
  setStops,
  picking,
  setPicking,
  timezone,
  simulating,
  onRun,
  canSave,
  onSave,
}: PlanFormProps) {
  const vehicles = useMemo(() => eldVehicles.filter((vehicle) => vehicle.active), [eldVehicles]);

  const driverOptions = useMemo(() => {
    const filtered = vehicleId
      ? driverStatuses.filter((driver) => driver.eldVehicleId === vehicleId)
      : driverStatuses;
    return [...filtered].sort((a, b) => driverLabel(a).localeCompare(driverLabel(b)));
  }, [driverStatuses, vehicleId]);

  const handleVehicleChange = (newVehicleId: number | null) => {
    setVehicleId(newVehicleId);
    if (!newVehicleId) {
      return;
    }
    const assigned = driverStatuses.filter((driver) => driver.eldVehicleId === newVehicleId);
    setDriverId(assigned.length === 1 ? assigned[0].eldDriverId : null);
  };

  const locationStale =
    !!selectedDriver?.locationAt &&
    DateTime.fromISO(selectedDriver.locationAt).diffNow('minutes').minutes <
      -LOCATION_STALE_MINUTES;
  const usingOverriddenOrigin = originOverride.lat !== null;

  const togglePick = (target: PickTarget) => setPicking(picking === target ? null : target);

  const updateStop = (index: number, changes: Partial<TripStop>) => {
    setStops(stops.map((stop, i) => (i === index ? { ...stop, ...changes } : stop)));
  };

  const clockField = (label: string, field: keyof SimClocks, limit: number) => (
    <Box sx={{ width: 110 }}>
      <Typography variant="caption" color="text.secondary">
        {label} (max {limit})
      </Typography>
      <TruxSimpleDecimalTwoField
        value={100 * clockEdits[field]}
        onChange={(val: number) =>
          setClockEdits({ ...clockEdits, [field]: Math.max(0, Math.min(limit, val / 100)) })
        }
      />
    </Box>
  );

  return (
    <Card sx={{ mb: 2, p: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Driver
          </Typography>
          <Stack direction="column" gap={1}>
            <TruxFormField label="Vehicle">
              <Autocomplete
                options={vehicles}
                getOptionLabel={(vehicle) => vehicle.vehicleUnitNumber || ''}
                value={vehicles.find((vehicle) => vehicle.id === vehicleId) || null}
                onChange={(_, vehicle) => handleVehicleChange(vehicle?.id || null)}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Any vehicle" />
                )}
                size="small"
              />
            </TruxFormField>

            <TruxFormField label="Driver">
              <Autocomplete
                options={driverOptions}
                getOptionLabel={driverLabel}
                value={selectedDriver}
                onChange={(_, driver) => setDriverId(driver?.eldDriverId || null)}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Pick a driver" />
                )}
                size="small"
              />
            </TruxFormField>

            {selectedDriver && (
              <TruxFormField label="Status">
                <Stack
                  gap={1}
                  direction="row"
                  flexWrap="wrap"
                  alignItems="center"
                  sx={{ pt: '5px' }}
                >
                  <EldDriverStatusLabel status={selectedDriver.dutyStatus as any} />
                  {!adjustHours && (
                    <>
                      <ClockChip label="Break" hours={selectedDriver.clocks.break} />
                      <ClockChip label="Drive" hours={selectedDriver.clocks.drive} />
                      <ClockChip label="Shift" hours={selectedDriver.clocks.shift} />
                      <ClockChip label="Cycle" hours={selectedDriver.clocks.cycle} />
                    </>
                  )}
                </Stack>
              </TruxFormField>
            )}

            {selectedDriver && (
              <TruxFormField
                label={
                  <>
                    Location
                    {selectedDriver.locationAt && !usingOverriddenOrigin && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block' }}
                        color={locationStale ? 'error.main' : 'text.secondary'}
                      >
                        located {DateTime.fromISO(selectedDriver.locationAt).toRelative()}
                        {locationStale && ' (stale)'}
                      </Typography>
                    )}
                  </>
                }
              >
                <LocationField
                  value={originOverride}
                  onChange={setOriginOverride}
                  picking={picking === 'origin'}
                  onTogglePick={() => togglePick('origin')}
                  placeholder={
                    selectedDriver.lat !== null
                      ? 'Using the live vehicle location, type to override'
                      : 'No known location, enter it or pick on the map'
                  }
                />
              </TruxFormField>
            )}

            <TruxFormField label="Departs">
              <TruxSimpleDateTimeField
                value={tripDepartAt || undefined}
                onChange={(date) => setTripDepartAt(date || null)}
                timezone={timezone}
                limitWidth={false}
              />
              {!tripDepartAt && (
                <Typography fontSize="small" fontStyle="italic">
                  leave empty to plan from now
                </Typography>
              )}
            </TruxFormField>

            <TruxFormField label="Hours">
              <Stack direction="column" gap={1} sx={{ pt: '2px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={adjustHours}
                      onChange={(e) => setAdjustHours(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Adjust the hours if the clocks look wrong
                    </Typography>
                  }
                />
                {adjustHours && (
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {clockField('Break', 'break', 8)}
                    {clockField('Drive', 'drive', 11)}
                    {clockField('Shift', 'shift', 14)}
                    {clockField('Cycle', 'cycle', 70)}
                  </Stack>
                )}
              </Stack>
            </TruxFormField>
          </Stack>
        </Grid>

        <Grid item xs={12} md={7}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Trip
          </Typography>
          <Stack direction="column" gap={2}>
            {stops.map((stop, index) => (
              <Box
                key={stop.key}
                component="div"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  opacity: stop.done ? 0.6 : 1,
                }}
              >
                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: stop.done ? 0 : 1 }}>
                  <Label
                    sx={{
                      color: 'white',
                      background: stop.label === 'PICKUP' ? '#288dff' : '#7635dc',
                    }}
                  >
                    Stop {index + 1}
                  </Label>
                  <Box sx={{ width: 140 }}>
                    <TruxSimpleSelectField
                      value={stop.label}
                      onChange={(label: StopLabel) => updateStop(index, { label })}
                      disabled={stop.done}
                    >
                      {Object.entries(STOP_LABELS).map(([value, text]) => (
                        <MenuItem key={value} value={value}>
                          {text}
                        </MenuItem>
                      ))}
                    </TruxSimpleSelectField>
                  </Box>
                  {stop.done && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {stop.location.address}
                    </Typography>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  {stop.plannedArrival && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={!!stop.done}
                          onChange={(e) => updateStop(index, { done: e.target.checked })}
                        />
                      }
                      label={<Typography variant="body2">Done</Typography>}
                      sx={{ mr: 0 }}
                    />
                  )}
                  <Tooltip title="Remove this stop">
                    <span>
                      <IconButton
                        size="small"
                        disabled={stops.length === 1}
                        onClick={() => setStops(stops.filter((_, i) => i !== index))}
                      >
                        <Iconify icon="mdi:trash-can-outline" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>

                {!stop.done && (
                  <Stack direction="column" gap={1}>
                    <TruxFormField label="Location">
                      <LocationField
                        value={stop.location}
                        onChange={(location) => updateStop(index, { location })}
                        picking={picking === index}
                        onTogglePick={() => togglePick(index)}
                      />
                    </TruxFormField>

                    <TruxFormField label="Appointment">
                      <TruxSimpleDateTimeField
                        value={stop.appointmentAt || undefined}
                        onChange={(date) => updateStop(index, { appointmentAt: date || null })}
                        timezone={timezone}
                        limitWidth={false}
                      />
                    </TruxFormField>

                    <TruxFormField label="Hours there">
                      <TruxSimpleDecimalTwoField
                        value={100 * stop.dwellHours}
                        onChange={(val: number) =>
                          updateStop(index, { dwellHours: Math.max(0, Math.min(24, val / 100)) })
                        }
                      />
                    </TruxFormField>

                    <TruxFormField label="Departs at">
                      <TruxSimpleDateTimeField
                        value={stop.departAt || undefined}
                        onChange={(date) => updateStop(index, { departAt: date || null })}
                        timezone={timezone}
                        limitWidth={false}
                      />
                      {!stop.departAt && (
                        <Typography fontSize="small" fontStyle="italic">
                          leave empty to head out as soon as they are done
                        </Typography>
                      )}
                    </TruxFormField>
                  </Stack>
                )}
              </Box>
            ))}

            <Divider />

            <Stack direction="row" gap={1} alignItems="center">
              <Button
                variant="soft"
                color="info"
                startIcon={<Iconify icon="mingcute:add-line" />}
                onClick={() => setStops([...stops, newStopAfter(stops)])}
                disabled={stops.length >= 6}
              >
                Add Stop
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip
                title={
                  canSave ? 'Download this plan as a file to track later' : 'Plan the trip first'
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<Iconify icon="solar:download-linear" />}
                    onClick={onSave}
                    disabled={!canSave || simulating}
                  >
                    Save the Trip
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" onClick={onRun} disabled={simulating}>
                {simulating ? (
                  <CircularProgress size={22} sx={{ color: 'white' }} />
                ) : (
                  <>Plan the Trip</>
                )}
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
}

// a delivery usually follows a pickup and the other way around
function newStopAfter(stops: TripStop[]) {
  const last = stops[stops.length - 1];
  return newTripStop(last?.label === 'PICKUP' ? 'DELIVERY' : 'PICKUP');
}
